import React, {
  useState, useEffect, ReactElement,
} from 'react';
import i18n, { InitOptions } from 'i18next';
import { initReactI18next } from 'react-i18next';
import HttpApi from 'i18next-http-backend';
import ICU from 'i18next-icu';
import { KatSpinner } from '@amzn/katal-react';
import LanguageDetector from 'i18next-browser-languagedetector';
import { logger } from 'src/utils';
import { Logs } from 'src/constants/metrics';
import languageMap from 'public/locales/i18n/forumsTranslationManifest.json';
import Backend from 'i18next-chained-backend';
import resourcesToBackend from 'i18next-resources-to-backend';
import FetchStatus from '../types/FetchStatus';

export const i18nInitOptions: InitOptions = {
  debug: process.env.NODE_ENV === 'development',
  fallbackLng: 'en-US',
  interpolation: {
  // not needed for react as it escapes by default
    escapeValue: false,
    format: (value, format, lng) => {
      if (format === 'compactNumber') {
        try {
          return new Intl.NumberFormat(lng, { notation: 'compact' }).format(value);
        } catch {
          let compactValue = value;
          let unit = '';
          if (value >= 1000000) {
            compactValue /= 1000000;
            unit = 'M';
          } else if (value >= 1000) {
            compactValue /= 1000;
            unit = 'K';
          }
          compactValue = Math.round(compactValue * 10) / 10;
          return `${compactValue}${unit}`;
        }
      }

      return value;
    },
  },
  load: 'currentOnly',
  backend: {
    /**
     * Setting up fallback to en-US for http-backend failures
     * ref - https://www.i18next.com/how-to/backend-fallback
     */
    backends: [
      HttpApi,
      resourcesToBackend(() => import('public/locales/i18n/en-US.json')),
    ],
    backendOptions: [{
      parse: (data: string) => {
        try {
          return JSON.parse(data);
        } catch (error) {
          if (error instanceof SyntaxError) {
            logger.error(Logs.i18nJSONParseFailure, {
              errorMessage: error.message,
            });
          }
          throw error;
        }
      },
      loadPath: (localeList: Array<keyof typeof languageMap>) => languageMap[localeList[0]] || languageMap['en-US'],
    }],
  },
  detection: {
  // order and from where user language should be detected
    order: ['htmlTag', 'navigator'],
  },
  keySeparator: false,
  // https://www.i18next.com/overview/typescript#argument-of-type-defaulttfuncreturn-is-not-assignable-to-parameter-of-type-xyz
  returnNull: false,
  react: {
    useSuspense: false,
  },
};

export const I18nWrapper: React.FC<{ children: ReactElement }> = ({ children }) => {
  const [i18nStatus, seti18nStatus] = useState<FetchStatus>(FetchStatus.loading);
  useEffect(() => {
    i18n
      .use(Backend)
      .use(ICU)
      .use(LanguageDetector)
      .use(initReactI18next)
      .init(
        i18nInitOptions,
        (err?: string) => {
          if (err) {
            logger.error(Logs.i18nFailure, { err });
          }
          return seti18nStatus(FetchStatus.complete);
        },
      );
  }, []);
  if (i18nStatus === FetchStatus.complete) {
    return children;
  }
  return <div data-testid="i18n-spinner"><KatSpinner /></div>;
};

export default I18nWrapper;
